// src/services/portfolioStatePersistence.js
//
// SessionStorage persistence for Portfolio view state (P6).
// Storage key: "ripple_portfolio_state_v1" (versioned per CLAUDE.md convention).
// SessionStorage semantics: survives navigation within a tab, cleared on tab close.
//
// Components never call sessionStorage directly — use loadPortfolioState()
// and savePortfolioState(state) instead.

import { log } from "../utils/logger.js";
import { M_AND_A_IDS } from "../utils/mAndA.js";
import { LEASE_TYPES } from "../utils/leaseTypeConstants.js";
import { SORT_KEYS, DEFAULT_SORT_KEY } from "../utils/sortLocations.js";

export const STORAGE_KEY           = "ripple_portfolio_state_v1";
export const LEASES_VIEW_KEY       = "ripple_leases_view_v1";
export const LEASES_SORT_KEY       = "ripple_leases_sort_v1";
export const LEASES_FILTERS_KEY    = "ripple_leases_filters_v1";
export const LOCATIONS_V2_VIEW_KEY = "ripple_locations_v2_view_v1";
// LIST-SORT-ROLLOUT-S1: shared toolbar SortControl state for the Leases v2
// page (LeasesPage.jsx). Separate from the legacy LEASES_SORT_KEY above,
// which belongs to the retired LeasesView surface.
export const LEASES_V2_SORT_KEY    = "ripple_leases_v2_sort_v1";

// ── localStorage keys (survive tab close; one per list) ───────────────────────
// sessionStorage keys above retain their semantics (within-tab survival only).
// These localStorage mirrors ensure sort + filter state survives full page reload.
export const LOCATIONS_LOCAL_KEY = "ripple_liststate_locations";
export const LEASES_LOCAL_KEY    = "ripple_liststate_leases";
export const PROJECTS_LOCAL_KEY  = "ripple_liststate_projects";

// ── UI preference keys (localStorage, survive browser restart) ─────────────────
// One versioned key per main object page. Bumping the suffix (e.g. _v2) resets
// all users to the page's hardcoded default — use when a breaking state-shape
// change ships. These are entirely separate from the ripple_liststate_* data
// keys above: clearing prefs must never touch those, and vice versa.
export const UI_PREFS_LEASES_V1   = "ui_prefs_leases_v1";
export const UI_PREFS_PLAN_V1     = "ui_prefs_plan_v1";
export const UI_PREFS_PROJECTS_V1 = "ui_prefs_projects_v1";
export const UI_PREFS_OFFICES_V1  = "ui_prefs_offices_v1";
export const UI_PREFS_RADAR_V1          = "ui_prefs_radar_v1";
export const UI_PREFS_INFOCUS_RADAR_V1  = "ui_prefs_infocus_radar_v1";

// ── Report view state keys (localStorage, survive browser restart) ─────────────
// One versioned key per report. Stores the last display state (view toggle,
// filters, search, sort, density) so returning to a report restores context.
// URL params always take precedence over localStorage on mount.
export const REPORT_SPACE_FIT_KEY  = "ripple_report_space_fit_v1";
export const REPORT_HC_TREND_KEY   = "ripple_report_hc_trend_v1";
export const REPORT_OPEN_ROLES_KEY = "ripple_report_open_roles_v1";
export const REPORT_LANDING_KEY    = "ripple_report_landing_v1";

// Load report view state from localStorage. Returns null on miss or parse error.
// Never throws. The stored blob is accepted as-is; field-level validation is the
// caller's responsibility (useReportViewState validates before applying).
export function loadReportViewState(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (err) {
    log.error(`portfolioStatePersistence: loadReportViewState failed key=${key}`, err);
    return null;
  }
}

// Persist report view state to localStorage. Never throws.
export function saveReportViewState(key, state) {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch (err) {
    log.error(`portfolioStatePersistence: saveReportViewState failed key=${key}`, err);
  }
}

// LSE-LEASES-LIST-DETAIL-NAV-RESPECTS-LIST-SORT
// Navigation-context keys: tracks which list view the user came from
// (so Location detail renders Lease nav vs Location nav), and which
// specific lease is the current waypoint in the nav sequence.
// SessionStorage semantics: survives within-tab navigation, cleared on close.
export const LAST_LIST_VIEW_KEY             = "ripple_last_list_view_v1";
export const CURRENT_NAV_LEASE_KEY          = "ripple_current_nav_lease_v1";
// task-849: locationId of the persisted nav lease, stored alongside the lease
// ID so openLocationFromPortfolio can match without waiting for allLeases to load.
export const CURRENT_NAV_LEASE_LOCATION_KEY = "ripple_current_nav_lease_location_v1";

// ── Shared sort-mode discriminator ────────────────────────────────────────────
// Both Locations List view and Leases List view use this to distinguish
// "dropdown is the active sort control" from "a column header click is
// the active sort control". Mutual-exclusion rule: only one indicator
// shows at a time (§6 — unified sort state, LSE-LIST-VIEW-SORT-UNIFICATION).
export const VALID_SORT_MODES = ["dropdown", "column"];

// ── Leases view persistence ───────────────────────────────────────────────────
// Separate key from the Locations portfolio state — the two pages are
// independent surfaces with different state shapes.
export const VALID_LEASES_VIEWS = ["timeline", "list", "action"];

// Sortable columns in LeasesListTable (must mirror COLUMNS with sortable:true).
// null = no column sort applied. Exported so LeasesPage can validate
// URL params without duplicating this list.
export const VALID_LEASES_LIST_SORT_KEYS = [null, "location", "region", "status", "annual_cost", "end_date", "m_and_a"];

// LSE-LANDING-STATUS-SORT-ORDER: "smart" added as the new default dropdown key.
const VALID_LEASES_DROPDOWN_KEYS = ["smart", "endDate", "annualCost", "daysRemaining"];
const VALID_LEASES_SORT_DIRS     = [1, -1];

// Returns the stored view string ("timeline" | "list"), or null when
// sessionStorage is empty or the stored value is unrecognised.
export function loadLeasesView() {
  try {
    const raw = window.sessionStorage.getItem(LEASES_VIEW_KEY);
    if (raw === null) return null;
    if (raw === "classic") return "timeline";
    if (VALID_LEASES_VIEWS.includes(raw)) return raw;
    log.error(`portfolioStatePersistence: invalid leases view=${JSON.stringify(raw)}, using default`);
    return null;
  } catch (err) {
    log.error("portfolioStatePersistence: loadLeasesView failed", err);
    return null;
  }
}

// Persists "timeline" | "list" to sessionStorage. Validates before writing.
// Never throws.
export function saveLeasesView(view) {
  try {
    const validView = VALID_LEASES_VIEWS.includes(view)
      ? view
      : (log.error(`portfolioStatePersistence: saveLeasesView invalid view=${JSON.stringify(view)}, using default`), VALID_LEASES_VIEWS[0]);
    window.sessionStorage.setItem(LEASES_VIEW_KEY, validView);
  } catch (err) {
    log.error("portfolioStatePersistence: saveLeasesView failed", err);
  }
}

// LSE-LOCATIONS-DETAIL-RENAME-AND-NEW-LIST-VIEW: "detail" added for the
// card-row view (was "list"). "list" now means the compact table view.
// Exported so PortfolioView can validate URL params without duplicating this list.
export const VALID_MODES       = ["detail", "list", "tiles", "map"];
// Sortable columns in LocationsListTable (must mirror COLUMNS with sortable:true).
// null = no column sort applied (table renders in portfolio-sort order).
// Exported so PortfolioView can validate URL params without duplicating this list.
// null is included for sessionStorage validation; URL validation guards separately
// via `urlSort && ...` so null never reaches the .includes() check from URL params.
// Must mirror COLUMNS with sortable:true in LocationsListTable.jsx.
// Keep in sync with sortLocationsColumn.js case labels.
export const VALID_LIST_SORT_KEYS = [null, "location", "health", "region", "sqft", "seats", "headcount", "assignedPct", "utilizationPct", "mAndA", "stage", "hub"];
const VALID_LIST_SORT_DIRS = [1, -1];
// Exported so PortfolioView can validate URL params for all filter dimensions.
// "off_track" is a filter-only meta-value meaning yellow OR red (not a stored
// location.health value); included here for the same reason "any"/"none" appear
// in VALID_MA_FILTER_VALUES — it's a valid selection in the UI filter layer.
export const VALID_HEALTH      = ["all", "green", "yellow", "red", "off_track"];
// LSE-VACATED — 'vacated' added to the persisted-filter allow-list so
// users who select "Vacated" in the Stage dropdown get that selection
// rehydrated on reload. L52 hygiene: must move in lockstep with the
// CHECK constraint in db/migrations/0032_add_vacated_stage.sql and the
// STAGES SSOT in src/app/tokens.js.
// LSE-LOCATIONS-STAGE-DROPDOWN-RESTRUCTURE: "all_stages" added (new meta-value).
// Task-340: "display_all"/"all_inactive" are normalised to "all" in validateFilters
// before reaching VALID_STAGES, so they no longer need to appear in this list.
// OFFICE-STAGE-REMOVE-NEGOTIATING-PROSPECTING: negotiating + prospecting kept in
// VALID_STAGES so saved/URL params that still carry those values are tolerated
// rather than silently broken. They map through as exact-stage filters (0 results
// for locations unless a legacy DB row still holds that value).
export const VALID_STAGES      = ["all", "all_stages", "draft", "build", "operational", "dormant", "vacated", "negotiating", "prospecting", "closed"];

// Maps legacy stage values (written before LSE-LOCATIONS-STAGE-DROPDOWN-RESTRUCTURE)
// to their canonical equivalents. Applied on both URL-param load (PortfolioView) and
// sessionStorage load (validateFilters) so returning users are never surprised.
// Exported so callers that read URL params can share the same map.
export const LEGACY_STAGE_NORMALISE = Object.freeze({ display_all: "all", all_inactive: "all" });
export const VALID_REGIONS     = ["all", "AMER", "EMEA", "APAC"];
// Single source of truth: snake_case values from leaseTypeConstants.js.
// "all" is the filter meta-value meaning "no filter applied".
export const VALID_LEASE_TYPES = ["all", ...LEASE_TYPES];
// Valid M&A filter values: special meta-values + canonical tag ids from mAndA.js.
// "all"/"any"/"none" are filter-only meta-values; they are NOT valid location.mAndA values.
// Exported as a Set for O(1) lookup in PortfolioView URL validation.
export const VALID_MA_FILTER_VALUES = new Set(["all", "any", "none", ...M_AND_A_IDS]);
// Valid primary sort keys: the selectable dropdown options (SORT_KEYS) plus the
// internal default (DEFAULT_SORT_KEY = "smart"), which is not in SORT_KEYS but
// must remain valid so existing persisted "smart" values survive without
// triggering the invalid-key error path.
const VALID_SORT_KEYS = [...SORT_KEYS, DEFAULT_SORT_KEY];

export const DEFAULT_PORTFOLIO_STATE = Object.freeze({
  filters: Object.freeze({
    health: "all", stage: "all", region: "all",
    mAndA: "all", leaseType: "all",
  }),
  mode: "detail",
  sortKey: "smart",
  // List-view column sort. null = unsorted (default), dir: 1=asc -1=desc.
  listSortKey: null,
  listSortDir: 1,
  // Unified sort discriminator (LSE-LIST-VIEW-SORT-UNIFICATION).
  // "dropdown" = SortDropdown is active; "column" = a column header click is active.
  sortMode: "dropdown",
});

function validateFilters(raw) {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PORTFOLIO_STATE.filters };
  const out = {};

  function pick(key, valid, fallback) {
    if (valid.includes(raw[key])) { out[key] = raw[key]; return; }
    log.error(`portfolioStatePersistence: invalid filters.${key}=${JSON.stringify(raw[key])}, using default`);
    out[key] = fallback;
  }

  // Normalise legacy stage values before validation so stored blobs from
  // before LSE-LOCATIONS-STAGE-DROPDOWN-RESTRUCTURE are transparently upgraded.
  if (raw.stage !== undefined && Object.prototype.hasOwnProperty.call(LEGACY_STAGE_NORMALISE, raw.stage)) {
    raw = { ...raw, stage: LEGACY_STAGE_NORMALISE[raw.stage] };
  }

  pick("health",    VALID_HEALTH,      "all");
  pick("stage",     VALID_STAGES,      "all");
  pick("region",    VALID_REGIONS,     "all");
  pick("leaseType", VALID_LEASE_TYPES, "all");

  // MA2 — Valid M&A filter values: use module-level VALID_MA_FILTER_VALUES Set.
  if (VALID_MA_FILTER_VALUES.has(raw.mAndA)) {
    out.mAndA = raw.mAndA;
  } else {
    log.error(`portfolioStatePersistence: invalid filters.mAndA=${JSON.stringify(raw.mAndA)}, using default`);
    out.mAndA = "all";
  }

  return out;
}

// Load and validate Portfolio state from sessionStorage.
// Returns the stored state on success, or null when sessionStorage is empty.
// Never throws — invalid individual fields fall back to defaults per-key.
export function loadPortfolioState() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    // Detect legacy stage value before validateFilters normalises it so we can
    // write the upgraded blob back immediately (one-time migration pattern,
    // mirrors loadLeasesSort). Without the write-back the warn re-fires on
    // every subsequent load for users who still have a stale blob stored.
    let migrated = false;
    if (
      parsed.filters &&
      typeof parsed.filters === "object" &&
      parsed.filters.stage !== undefined &&
      Object.prototype.hasOwnProperty.call(LEGACY_STAGE_NORMALISE, parsed.filters.stage)
    ) {
      log.warn(
        `portfolioStatePersistence: loadPortfolioState migrating stale stage '${parsed.filters.stage}' → '${LEGACY_STAGE_NORMALISE[parsed.filters.stage]}'`,
      );
      migrated = true;
    }

    // LSE-LOCATIONS-SORT-REMOVE-ALPHABETICAL: "alphabetical" was removed from
    // the Sort dropdown. Rewrite any persisted blob that still carries this
    // key to "smart" before the allowlist check so the value is never silently
    // discarded — it is explicitly migrated instead.
    if (parsed.sortKey === "alphabetical") {
      log.warn("portfolioStatePersistence: loadPortfolioState migrating stale sortKey 'alphabetical' → 'smart'");
      parsed.sortKey = "smart";
      migrated = true;
    }

    const mode = VALID_MODES.includes(parsed.mode)
      ? parsed.mode
      : (log.error(`portfolioStatePersistence: invalid mode=${JSON.stringify(parsed.mode)}, using default`), DEFAULT_PORTFOLIO_STATE.mode);

    const sortKey = VALID_SORT_KEYS.includes(parsed.sortKey)
      ? parsed.sortKey
      : (log.error(`portfolioStatePersistence: invalid sortKey=${JSON.stringify(parsed.sortKey)}, using default`), DEFAULT_PORTFOLIO_STATE.sortKey);

    const listSortKey = VALID_LIST_SORT_KEYS.includes(parsed.listSortKey)
      ? parsed.listSortKey
      : (log.error(`portfolioStatePersistence: invalid listSortKey=${JSON.stringify(parsed.listSortKey)}, using default`), DEFAULT_PORTFOLIO_STATE.listSortKey);

    const listSortDir = VALID_LIST_SORT_DIRS.includes(parsed.listSortDir)
      ? parsed.listSortDir
      : (log.error(`portfolioStatePersistence: invalid listSortDir=${JSON.stringify(parsed.listSortDir)}, using default`), DEFAULT_PORTFOLIO_STATE.listSortDir);

    // sortMode: new field; old stored blobs won't have it — fall back silently.
    const sortMode = VALID_SORT_MODES.includes(parsed.sortMode)
      ? parsed.sortMode
      : DEFAULT_PORTFOLIO_STATE.sortMode;

    const filters = validateFilters(parsed.filters);

    const result = { filters, mode, sortKey, listSortKey, listSortDir, sortMode };

    if (migrated) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));
      } catch {
        // Write failure (quota / private-browsing) is non-fatal; return the
        // migrated value anyway. The warn will re-fire on the next load but
        // that is preferable to losing the value entirely.
      }
    }

    return result;
  } catch (err) {
    log.error("portfolioStatePersistence: load failed", err);
    return null;
  }
}

// Persist Portfolio state to sessionStorage.
// All fields are validated and normalised to defaults before writing so
// corrupt data can never enter sessionStorage (mirrors saveLeasesFilters).
// Never throws — logs [Error] and continues on quota/security failures.
export function savePortfolioState({ filters, mode, sortKey, listSortKey, listSortDir, sortMode }) {
  try {
    const validatedFilters = validateFilters(filters);

    const validMode = VALID_MODES.includes(mode)
      ? mode
      : (log.error(`portfolioStatePersistence: savePortfolioState invalid mode=${JSON.stringify(mode)}, using default`), DEFAULT_PORTFOLIO_STATE.mode);

    const validSortKey = VALID_SORT_KEYS.includes(sortKey)
      ? sortKey
      : (log.error(`portfolioStatePersistence: savePortfolioState invalid sortKey=${JSON.stringify(sortKey)}, using default`), DEFAULT_PORTFOLIO_STATE.sortKey);

    const validListSortKey = VALID_LIST_SORT_KEYS.includes(listSortKey)
      ? listSortKey
      : (log.error(`portfolioStatePersistence: savePortfolioState invalid listSortKey=${JSON.stringify(listSortKey)}, using default`), DEFAULT_PORTFOLIO_STATE.listSortKey);

    const validListSortDir = VALID_LIST_SORT_DIRS.includes(listSortDir)
      ? listSortDir
      : (log.error(`portfolioStatePersistence: savePortfolioState invalid listSortDir=${JSON.stringify(listSortDir)}, using default`), DEFAULT_PORTFOLIO_STATE.listSortDir);

    const validSortMode = VALID_SORT_MODES.includes(sortMode)
      ? sortMode
      : (log.error(`portfolioStatePersistence: savePortfolioState invalid sortMode=${JSON.stringify(sortMode)}, using default`), DEFAULT_PORTFOLIO_STATE.sortMode);

    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        filters: validatedFilters,
        mode: validMode,
        sortKey: validSortKey,
        listSortKey: validListSortKey,
        listSortDir: validListSortDir,
        sortMode: validSortMode,
      }),
    );
  } catch (err) {
    log.error("portfolioStatePersistence: save failed", err);
  }
}

// ── Leases filter persistence ──────────────────────────────────────────────────
// Separate key from sort and view — filter state is its own dimension.
// SessionStorage semantics: survives within-tab navigation, cleared on tab close.

// LSE-LEASES-STATUS-DROPDOWN-PARITY-WITH-LOCATIONS: "all_statuses" added (show-everything meta-value).
// LSE-DRAFT-LEASE-STATUS: "draft" added (placeholder leases, excluded from all_active).
// STAGE-VS-LEASE-STATUS-DECOUPLE: "vacated" renamed to "vacant_held" (label +
// key) so the lease-status bucket no longer collides with location.stage's
// "Vacated" value; the two are different axes. See loadLeasesFilters() below
// for backward-compat migration of any already-persisted "vacated" value.
export const VALID_LEASE_STATUSES   = ["all_active", "all_statuses", "all", "draft", "pre_commencement", "in_use", "vacant_held", "expired"];
// LEASES-ACTION-VIEW S1: focus and horizon are Action-view display preferences
// persisted in the filter object so URL sharing and session storage cover them.
export const VALID_FOCUS_WINDOWS    = ["90d", "12mo", "24mo", "all"];
export const VALID_HORIZON_WINDOWS  = ["3yr", "5yr", "full"];

export const VALID_EXPIRY_BANDS     = ["all", "lt1", "1to3", "3to5", "gt5"];

export const DEFAULT_LEASES_FILTERS = Object.freeze({
  status:    "all_active",
  region:    "all",
  leaseType: "all",
  mAndA:     "all",
  expiry:    "all",
  focus:     "24mo",
  horizon:   "full",
});

function validateLeasesFilters(src) {
  if (!src || typeof src !== "object") return { ...DEFAULT_LEASES_FILTERS };
  const out = {};

  function pick(key, valid, fallback, rawOverride) {
    const value = rawOverride !== undefined ? rawOverride : src[key];
    if (valid.includes(value)) { out[key] = value; return; }
    log.error(`portfolioStatePersistence: invalid leases filters.${key}=${JSON.stringify(src[key])}, using default`);
    out[key] = fallback;
  }

  // STAGE-VS-LEASE-STATUS-DECOUPLE: migrate any already-persisted "vacated"
  // lease-status selection (pre-rename) to "vacant_held" so returning users
  // keep their selected filter instead of silently falling back to default.
  const rawStatus = src.status === "vacated" ? "vacant_held" : src.status;
  pick("status",    VALID_LEASE_STATUSES,  "all_active", rawStatus);
  pick("region",    VALID_REGIONS,         "all");
  pick("leaseType", VALID_LEASE_TYPES,     "all");
  pick("expiry",    VALID_EXPIRY_BANDS,    "all");
  // LEASES-ACTION-VIEW S1: focus and horizon persisted in the filter object.
  pick("focus",     VALID_FOCUS_WINDOWS,   "24mo");
  pick("horizon",   VALID_HORIZON_WINDOWS, "full");

  if (VALID_MA_FILTER_VALUES.has(src.mAndA)) {
    out.mAndA = src.mAndA;
  } else {
    log.error(`portfolioStatePersistence: invalid leases filters.mAndA=${JSON.stringify(src.mAndA)}, using default`);
    out.mAndA = "all";
  }

  return out;
}

export function loadLeasesFilters() {
  try {
    const raw = window.sessionStorage.getItem(LEASES_FILTERS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return validateLeasesFilters(parsed);
  } catch (err) {
    log.error("portfolioStatePersistence: loadLeasesFilters failed", err);
    return null;
  }
}

export function saveLeasesFilters(filters) {
  try {
    const validated = validateLeasesFilters(filters);
    window.sessionStorage.setItem(LEASES_FILTERS_KEY, JSON.stringify(validated));
  } catch (err) {
    log.error("portfolioStatePersistence: saveLeasesFilters failed", err);
  }
}

// Parse and validate leases filter state from URL search params.
// Returns a filters object when at least one recognised filter param is present,
// or null when no filter params are found (caller falls back to sessionStorage).
// Invalid individual values are silently ignored and fall back to the default for
// that key — same defensive behaviour as loadLeasesFilters().
export function loadLeasesFiltersFromUrl(params) {
  const rawStatus    = params.get("status");
  const rawRegion    = params.get("region");
  const rawLeaseType = params.get("leaseType");
  const rawMAndA     = params.get("mAndA");
  // LEASES-ACTION-VIEW S1: focus and horizon are URL-shareable filter params.
  const rawFocus     = params.get("focus");
  const rawHorizon   = params.get("horizon");

  if (!rawStatus && !rawRegion && !rawLeaseType && !rawMAndA &&
      !rawFocus && !rawHorizon) return null;

  const out = { ...DEFAULT_LEASES_FILTERS };

  if (rawStatus    && VALID_LEASE_STATUSES.includes(rawStatus))         out.status    = rawStatus;
  if (rawRegion    && VALID_REGIONS.includes(rawRegion))                out.region    = rawRegion;
  if (rawLeaseType && VALID_LEASE_TYPES.includes(rawLeaseType))         out.leaseType = rawLeaseType;
  if (rawMAndA     && VALID_MA_FILTER_VALUES.has(rawMAndA))             out.mAndA     = rawMAndA;
  if (rawFocus     && VALID_FOCUS_WINDOWS.includes(rawFocus))           out.focus     = rawFocus;
  if (rawHorizon   && VALID_HORIZON_WINDOWS.includes(rawHorizon))       out.horizon   = rawHorizon;

  return out;
}

// ── Leases sort persistence ────────────────────────────────────────────────────
// Separate key from view preference (ripple_leases_view_v1) — sort state is
// independent of which view is active. Per LSE-LIST-VIEW-SORT-UNIFICATION,
// sort does NOT persist across page navigation; this key is used for
// within-tab survival only (sessionStorage semantics).

export function loadLeasesSort() {
  try {
    const raw = window.sessionStorage.getItem(LEASES_SORT_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    // LSE-LANDING-STATUS-SORT-ORDER: 'smart' is now a valid key — removed
    // the old migration that downgraded it to 'endDate'. Old 'default' values
    // (pre-LSE-LANDING-PAGE-FOLLOWUP-2) still migrate to 'smart'.
    let migrated = false;
    if (parsed.sortKey === "default") {
      log.warn("portfolioStatePersistence: loadLeasesSort migrating stale sortKey 'default' → 'smart'");
      parsed.sortKey = "smart";
      migrated = true;
    }
    const sortKey = VALID_LEASES_DROPDOWN_KEYS.includes(parsed.sortKey)
      ? parsed.sortKey : "smart";
    const leaseSortKey = VALID_LEASES_LIST_SORT_KEYS.includes(parsed.leaseSortKey)
      ? parsed.leaseSortKey : null;
    const leaseSortDir = VALID_LEASES_SORT_DIRS.includes(parsed.leaseSortDir)
      ? parsed.leaseSortDir : 1;
    const sortMode = VALID_SORT_MODES.includes(parsed.sortMode)
      ? parsed.sortMode : "dropdown";

    if (migrated) {
      try {
        window.sessionStorage.setItem(
          LEASES_SORT_KEY,
          JSON.stringify({ sortKey, leaseSortKey, leaseSortDir, sortMode }),
        );
      } catch {
        // Write failure (quota / private-browsing) is non-fatal; return the
        // migrated value anyway. The warn will re-fire on the next load but
        // that is preferable to losing the value entirely.
      }
    }

    return { sortKey, leaseSortKey, leaseSortDir, sortMode };
  } catch (err) {
    log.error("portfolioStatePersistence: loadLeasesSort failed", err);
    return null;
  }
}

export function saveLeasesSort({ sortKey, leaseSortKey, leaseSortDir, sortMode }) {
  try {
    const validSortKey = VALID_LEASES_DROPDOWN_KEYS.includes(sortKey)
      ? sortKey
      : (log.error(`portfolioStatePersistence: saveLeasesSort invalid sortKey=${JSON.stringify(sortKey)}, using default`), "smart");

    const validLeaseSortKey = VALID_LEASES_LIST_SORT_KEYS.includes(leaseSortKey)
      ? leaseSortKey
      : (log.error(`portfolioStatePersistence: saveLeasesSort invalid leaseSortKey=${JSON.stringify(leaseSortKey)}, using default`), null);

    const validLeaseSortDir = VALID_LEASES_SORT_DIRS.includes(leaseSortDir)
      ? leaseSortDir
      : (log.error(`portfolioStatePersistence: saveLeasesSort invalid leaseSortDir=${JSON.stringify(leaseSortDir)}, using default`), 1);

    const validSortMode = VALID_SORT_MODES.includes(sortMode)
      ? sortMode
      : (log.error(`portfolioStatePersistence: saveLeasesSort invalid sortMode=${JSON.stringify(sortMode)}, using default`), "dropdown");

    window.sessionStorage.setItem(
      LEASES_SORT_KEY,
      JSON.stringify({ sortKey: validSortKey, leaseSortKey: validLeaseSortKey, leaseSortDir: validLeaseSortDir, sortMode: validSortMode }),
    );
  } catch (err) {
    log.error("portfolioStatePersistence: saveLeasesSort failed", err);
  }
}


// ── Navigation context persistence ────────────────────────────────────────────
// LSE-LEASES-LIST-DETAIL-NAV-RESPECTS-LIST-SORT
//
// lastListView: "leases" | "locations" — which list the user navigated from.
// currentNavLease: a lease ID string — the specific lease in the nav sequence.
//
// Both keys are sessionStorage (cleared on tab close). The defaults are
// backward-compatible: missing LAST_LIST_VIEW_KEY falls back to "locations"
// (existing Location nav behavior); missing CURRENT_NAV_LEASE_KEY falls back
// to the primary-lease logic in LeaseSummaryTab.

const VALID_LIST_VIEWS = ["leases", "locations"];

export function loadLastListView() {
  try {
    const raw = window.sessionStorage.getItem(LAST_LIST_VIEW_KEY);
    if (raw === null) return null;
    if (VALID_LIST_VIEWS.includes(raw)) return raw;
    log.error(`portfolioStatePersistence: invalid lastListView=${JSON.stringify(raw)}, using default`);
    return null;
  } catch (err) {
    log.error("portfolioStatePersistence: loadLastListView failed", err);
    return null;
  }
}

export function saveLastListView(view) {
  try {
    const valid = VALID_LIST_VIEWS.includes(view)
      ? view
      : (log.error(`portfolioStatePersistence: saveLastListView invalid view=${JSON.stringify(view)}, using default`), "locations");
    window.sessionStorage.setItem(LAST_LIST_VIEW_KEY, valid);
  } catch (err) {
    log.error("portfolioStatePersistence: saveLastListView failed", err);
  }
}

export function loadCurrentNavLease() {
  try {
    const raw = window.sessionStorage.getItem(CURRENT_NAV_LEASE_KEY);
    return raw || null;
  } catch (err) {
    log.error("portfolioStatePersistence: loadCurrentNavLease failed", err);
    return null;
  }
}

export function saveCurrentNavLease(leaseId) {
  try {
    if (!leaseId || typeof leaseId !== "string") {
      log.error(`portfolioStatePersistence: saveCurrentNavLease invalid leaseId=${JSON.stringify(leaseId)}`);
      return;
    }
    window.sessionStorage.setItem(CURRENT_NAV_LEASE_KEY, leaseId);
  } catch (err) {
    log.error("portfolioStatePersistence: saveCurrentNavLease failed", err);
  }
}

// task-849: persist the locationId of the current nav lease so
// openLocationFromPortfolio can match without needing allLeases to be loaded.
export function saveCurrentNavLeaseLocation(locationId) {
  try {
    if (!locationId || typeof locationId !== "string") {
      log.error(`portfolioStatePersistence: saveCurrentNavLeaseLocation invalid locationId=${JSON.stringify(locationId)}`);
      return;
    }
    window.sessionStorage.setItem(CURRENT_NAV_LEASE_LOCATION_KEY, locationId);
  } catch (err) {
    log.error("portfolioStatePersistence: saveCurrentNavLeaseLocation failed", err);
  }
}

export function loadCurrentNavLeaseLocation() {
  try {
    const raw = window.sessionStorage.getItem(CURRENT_NAV_LEASE_LOCATION_KEY);
    return raw || null;
  } catch (err) {
    log.error("portfolioStatePersistence: loadCurrentNavLeaseLocation failed", err);
    return null;
  }
}

export function clearCurrentNavLease() {
  try {
    window.sessionStorage.removeItem(CURRENT_NAV_LEASE_KEY);
    window.sessionStorage.removeItem(CURRENT_NAV_LEASE_LOCATION_KEY);
  } catch (err) {
    log.error("portfolioStatePersistence: clearCurrentNavLease failed", err);
  }
}

// ── Project filter validation ─────────────────────────────────────────────────
// Used by loadProjectsLocalState / saveProjectsLocalState.
// Valid project type values mirror PROJECT_TYPE keys from tokens.js.
// Valid phase values mirror PROJECT_PHASE keys from tokens.js.
// Valid health values mirror PROJECT_STATUS keys from tokens.js.
// Valid region values are shared with VALID_REGIONS above.
// owner is free-form (user-entered) — any non-empty string or "all" is valid.
const VALID_PROJECT_TYPES  = ["all", "new_build", "renovation", "critical_infra", "compliance", "exit", "consolidation", "m_and_a"];
const VALID_PROJECT_PHASES = ["all", "intake", "business_case", "planning", "design", "procurement", "build", "move_launch", "closeout", "complete"];
const VALID_PROJECT_HEALTH = ["all", "on_track", "at_risk", "critical"];
const VALID_PROJECT_SORT_DIRS = [1, -1];
// targetCompletionDate remains accepted for saved-preference compatibility;
// new project-date sorts use fdobDate.
export const VALID_PROJECT_SORT_KEYS = [null, "name", "locationName", "projectType", "phase", "fdobDate", "targetCompletionDate", "approvedBudget", "statusOverall"];

export const DEFAULT_PROJECT_LOCAL_FILTERS = Object.freeze({
  type: "all", region: "all", phase: "all", health: "all", owner: "all",
});

function validateProjectFilters(raw) {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PROJECT_LOCAL_FILTERS };
  const out = {};

  function pick(key, valid, fallback) {
    if (valid.includes(raw[key])) { out[key] = raw[key]; return; }
    log.error(`portfolioStatePersistence: invalid project filters.${key}=${JSON.stringify(raw[key])}, using default`);
    out[key] = fallback;
  }

  pick("type",   VALID_PROJECT_TYPES,  "all");
  pick("region", VALID_REGIONS,        "all");
  pick("phase",  VALID_PROJECT_PHASES, "all");
  pick("health", VALID_PROJECT_HEALTH, "all");

  // owner is free-form; accept any non-empty string or "all"
  if (raw.owner === "all" || (typeof raw.owner === "string" && raw.owner.length > 0)) {
    out.owner = raw.owner;
  } else {
    log.error(`portfolioStatePersistence: invalid project filters.owner=${JSON.stringify(raw.owner)}, using default`);
    out.owner = "all";
  }

  return out;
}

// ── Locations localStorage persistence ───────────────────────────────────────
// Mirrors the same state shape as savePortfolioState / loadPortfolioState.
// sessionStorage wins on conflict (loadLocationsLocalState is the fallback).

export function loadLocationsLocalState() {
  try {
    const raw = window.localStorage.getItem(LOCATIONS_LOCAL_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const filters = validateFilters(parsed.filters);
    const mode = VALID_MODES.includes(parsed.mode)
      ? parsed.mode : DEFAULT_PORTFOLIO_STATE.mode;
    const sortKey = VALID_SORT_KEYS.includes(parsed.sortKey)
      ? parsed.sortKey : DEFAULT_PORTFOLIO_STATE.sortKey;
    const listSortKey = VALID_LIST_SORT_KEYS.includes(parsed.listSortKey)
      ? parsed.listSortKey : DEFAULT_PORTFOLIO_STATE.listSortKey;
    const listSortDir = VALID_LIST_SORT_DIRS.includes(parsed.listSortDir)
      ? parsed.listSortDir : DEFAULT_PORTFOLIO_STATE.listSortDir;
    const sortMode = VALID_SORT_MODES.includes(parsed.sortMode)
      ? parsed.sortMode : DEFAULT_PORTFOLIO_STATE.sortMode;

    return { filters, mode, sortKey, listSortKey, listSortDir, sortMode };
  } catch (err) {
    log.error("portfolioStatePersistence: loadLocationsLocalState failed", err);
    return null;
  }
}

export function saveLocationsLocalState({ filters, mode, sortKey, listSortKey, listSortDir, sortMode }) {
  try {
    const validatedFilters = validateFilters(filters);
    const validMode = VALID_MODES.includes(mode) ? mode : DEFAULT_PORTFOLIO_STATE.mode;
    const validSortKey = VALID_SORT_KEYS.includes(sortKey) ? sortKey : DEFAULT_PORTFOLIO_STATE.sortKey;
    const validListSortKey = VALID_LIST_SORT_KEYS.includes(listSortKey)
      ? listSortKey : DEFAULT_PORTFOLIO_STATE.listSortKey;
    const validListSortDir = VALID_LIST_SORT_DIRS.includes(listSortDir)
      ? listSortDir : DEFAULT_PORTFOLIO_STATE.listSortDir;
    const validSortMode = VALID_SORT_MODES.includes(sortMode)
      ? sortMode : DEFAULT_PORTFOLIO_STATE.sortMode;
    window.localStorage.setItem(
      LOCATIONS_LOCAL_KEY,
      JSON.stringify({
        filters: validatedFilters, mode: validMode, sortKey: validSortKey,
        listSortKey: validListSortKey, listSortDir: validListSortDir, sortMode: validSortMode,
      }),
    );
  } catch (err) {
    log.error("portfolioStatePersistence: saveLocationsLocalState failed", err);
  }
}

// ── Leases localStorage persistence ──────────────────────────────────────────
// Combines filters + sort into a single blob (simpler than the two sessionStorage keys).
// sessionStorage wins on conflict (loadLeasesLocalState is the fallback).

export function loadLeasesLocalState() {
  try {
    const raw = window.localStorage.getItem(LEASES_LOCAL_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const filters = validateLeasesFilters(parsed.filters);
    const leaseSortKey = VALID_LEASES_LIST_SORT_KEYS.includes(parsed.leaseSortKey)
      ? parsed.leaseSortKey : null;
    const leaseSortDir = VALID_LEASES_SORT_DIRS.includes(parsed.leaseSortDir)
      ? parsed.leaseSortDir : 1;
    const sortMode = VALID_SORT_MODES.includes(parsed.sortMode)
      ? parsed.sortMode : "dropdown";

    return { filters, leaseSortKey, leaseSortDir, sortMode };
  } catch (err) {
    log.error("portfolioStatePersistence: loadLeasesLocalState failed", err);
    return null;
  }
}

export function saveLeasesLocalState({ filters, leaseSortKey, leaseSortDir, sortMode }) {
  try {
    const validatedFilters = validateLeasesFilters(filters);
    const validLeaseSortKey = VALID_LEASES_LIST_SORT_KEYS.includes(leaseSortKey)
      ? leaseSortKey : null;
    const validLeaseSortDir = VALID_LEASES_SORT_DIRS.includes(leaseSortDir)
      ? leaseSortDir : 1;
    const validSortMode = VALID_SORT_MODES.includes(sortMode)
      ? sortMode : "dropdown";
    window.localStorage.setItem(
      LEASES_LOCAL_KEY,
      JSON.stringify({
        filters: validatedFilters,
        leaseSortKey: validLeaseSortKey,
        leaseSortDir: validLeaseSortDir,
        sortMode: validSortMode,
      }),
    );
  } catch (err) {
    log.error("portfolioStatePersistence: saveLeasesLocalState failed", err);
  }
}

// ── Projects localStorage persistence ────────────────────────────────────────
// Projects has no sessionStorage equivalent — localStorage is the sole store.

const VALID_PROJECT_VIEW_MODES = ["list", "board"];
const VALID_PROJECT_SCOPES = ["capital_moves", "all"];

export function loadProjectsLocalState() {
  try {
    const raw = window.localStorage.getItem(UI_PREFS_PROJECTS_V1)
      || window.localStorage.getItem(PROJECTS_LOCAL_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    // PROJECTS-LANDING-ALIGN-TO-OFFICES: new format uses cfSelections (multi-select).
    // Old blobs carry `filters` (single-select); treat them as no active filter.
    const cfSelections = (parsed.cfSelections && typeof parsed.cfSelections === "object")
      ? parsed.cfSelections
      : {};

    const projectSortKey = VALID_PROJECT_SORT_KEYS.includes(parsed.projectSortKey)
      ? parsed.projectSortKey : null;
    const projectSortDir = VALID_PROJECT_SORT_DIRS.includes(parsed.projectSortDir)
      ? parsed.projectSortDir : 1;

    const viewMode = VALID_PROJECT_VIEW_MODES.includes(parsed.viewMode)
      ? parsed.viewMode : "list";
    const scope = VALID_PROJECT_SCOPES.includes(parsed.scope)
      ? parsed.scope : "all";

    return { cfSelections, projectSortKey, projectSortDir, viewMode, scope };
  } catch (err) {
    log.error("portfolioStatePersistence: loadProjectsLocalState failed", err);
    return null;
  }
}

export function saveProjectsLocalState({ cfSelections, projectSortKey, projectSortDir, viewMode, scope }) {
  try {
    // cfSelections is { [dimensionId]: string[] } — store as-is (no strict validation needed).
    const validCfSelections = (cfSelections && typeof cfSelections === "object") ? cfSelections : {};
    const validSortKey = VALID_PROJECT_SORT_KEYS.includes(projectSortKey)
      ? projectSortKey : null;
    const validSortDir = VALID_PROJECT_SORT_DIRS.includes(projectSortDir)
      ? projectSortDir : 1;
    const validViewMode = VALID_PROJECT_VIEW_MODES.includes(viewMode) ? viewMode : "list";
    const validScope = VALID_PROJECT_SCOPES.includes(scope) ? scope : "all";
    window.localStorage.setItem(
      UI_PREFS_PROJECTS_V1,
      JSON.stringify({
        cfSelections: validCfSelections,
        projectSortKey: validSortKey,
        projectSortDir: validSortDir,
        viewMode: validViewMode,
        scope: validScope,
      }),
    );
  } catch (err) {
    log.error("portfolioStatePersistence: saveProjectsLocalState failed", err);
  }
}

// ── Test utility: clear all guarded sessionStorage keys ───────────────────────
// Use this in test beforeEach hooks instead of window.sessionStorage.clear().
// The ESLint no-restricted-syntax rule bans direct clear() calls in test files
// because it would silently wipe all guarded keys at once — this function does
// the same thing but in a controlled, explicit way.
export function clearPersistedState() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(LEASES_VIEW_KEY);
    window.sessionStorage.removeItem(LEASES_SORT_KEY);
    window.sessionStorage.removeItem(LEASES_FILTERS_KEY);
    window.sessionStorage.removeItem(LAST_LIST_VIEW_KEY);
    window.sessionStorage.removeItem(CURRENT_NAV_LEASE_KEY);
    window.sessionStorage.removeItem(CURRENT_NAV_LEASE_LOCATION_KEY);
    window.sessionStorage.removeItem(LOCATIONS_V2_VIEW_KEY);
    // Also clear localStorage list-state mirrors
    window.localStorage.removeItem(LOCATIONS_LOCAL_KEY);
    window.localStorage.removeItem(LEASES_LOCAL_KEY);
    window.localStorage.removeItem(PROJECTS_LOCAL_KEY);
    // Clear UI preference keys (per-page localStorage prefs)
    window.localStorage.removeItem(UI_PREFS_LEASES_V1);
    window.localStorage.removeItem(UI_PREFS_PLAN_V1);
    window.localStorage.removeItem(UI_PREFS_PROJECTS_V1);
    window.localStorage.removeItem(UI_PREFS_OFFICES_V1);
  } catch {
    // ignore — test environment may not support sessionStorage/localStorage
  }
}
